import dynamic from 'next/dynamic';
import Head from "@modules/common/components/head";
// import Hero from "@modules/home/hero";
import { Homepage } from "types/homepage";
import { PosSystem } from "types/posSystem";
import { Restaurant } from "types/restaurant";


const Hero = dynamic(() => import("@modules/home/hero"));
const Statement = dynamic(() => import("@modules/home/statement"));
const Business = dynamic(() => import("@modules/home/business"));
const Customization = dynamic(() => import("@modules/home/customization"));
const PosSystems = dynamic(() => import("@modules/home/pos-systems"));
const OurClients = dynamic(() => import("@modules/home/our-clients"));
const DemoForm = dynamic(() => import("@modules/home/contact-form"));


export default function Home({
  restaurants,
  systems,
  homepage,
}: {
  restaurants: Restaurant[];
  systems: PosSystem[];
  homepage: Homepage;
}) {
  return (
    <div className="flex flex-col gap-20 lg:gap-32 lg:pt-16">
      <Head />
      <Hero
        data={{
          heroSloganPart1: homepage.heroSloganPart1,
          heroSloganPart2: homepage.heroSloganPart2,
          heroBody: homepage.heroBody,
          heroImages: homepage.heroImages,
        }}
      />
      <Statement
        data={{
          statementProfitableText: homepage.statementProfitableText,
          statementDataDriventext: homepage.statementDataDriventext,
          statementPerfectMomentText: homepage.statementPerfectMomentText,
          statementWithoutManualWorkText:
            homepage.statementWithoutManualWorkText,
          statementSpotlighText: homepage.statementSpotlighText,
        }}
      />
      <Business
        data={{
          businessSectionTitle: homepage.businessSectionTitle,
          businessSectionText: homepage.businessSectionText,
          businessSectionToggles: homepage.businessSectionToggles,
        }}
      />
      <Customization
        data={{
          customerSectionTitle: homepage.customerSectionTitle,
          customerSectionText: homepage.customerSectionText,
          customerSectionChooseColorText:
            homepage.customerSectionChooseColorText,
          customerSectionChooseColorTitle:
            homepage.customerSectionChooseColorTitle,
          steps: homepage.steps,
        }}
      />
      <PosSystems systems={systems} />
      <OurClients restaurants={restaurants} />
      <DemoForm />
    </div>
  );
}

export async function getStaticProps({ locale }: { locale?: string }) {
  // Strip trailing slash — Strapi 5 rejects `//api/...` with 400 "Malicious Path",
  // and that response still parses as JSON so the try/catch never fires.
  const strapiUrl = process.env.NEXT_PUBLIC_STRAPI_URL?.replace(/\/+$/, "");
  const headers = {
    Authorization: `Bearer ${process.env.NEXT_PUBLIC_STRAPI_TOKEN}`,
  };
  // We source all translatable copy from local i18n dictionaries, so Strapi
  // is only queried for the default locale. This avoids the Arabic Strapi
  // locale returning empty arrays (which would hide whole sections) when
  // CMS entries haven't been translated.
  void locale;

  try {

    const [restaurantsRes, homepageRes, systemsRes] = await Promise.all([
       fetch(`${strapiUrl}/api/restaurants?populate=*&sort[0]=sortingIndex`, {
        headers,
      }),
      fetch(
        `${strapiUrl}/api/homepage?populate=businessSectionToggles.icon&populate=businessSectionToggles.image&populate=heroImages&populate=steps.imageColor1&populate=steps.imageColor2&populate=steps.imageColor3`,
        { headers }
      ),
       fetch(`${strapiUrl}/api/systems?populate=*`, { headers }),
    ]);

    const [restaurantsData, homepageData, systemsData] = await Promise.all([
      restaurantsRes.json(),
      homepageRes.json(),
      systemsRes.json(),
    ]);

    return {
      props: {
        restaurants: restaurantsData?.data ?? [],
        homepage: homepageData?.data ?? {},
        systems: systemsData?.data ?? [],
      },
      revalidate: 60,
    };
  } catch (error) {
    console.error("Error fetching data from Strapi:", error);
    return {
      props: { restaurants: [], homepage: {}, systems: [] },
      revalidate: 60,
    };
  }
}